import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";

import {
  DEFAULT_USER_NOTES_LIMIT,
  userNotesQueryKey,
  type ListUserNotesResponse,
  type Note,
} from "./types";

type StoredToken = {
  AccessToken?: string;
};

export type UseGetUserNotesOptions = {
  limit?: number;
  lastEvaluatedKey?: string | null;
  includeDeleted?: boolean;
};

export type GetUserNotesResult = {
  notes: Note[];
  lastEvaluatedKey: string | null;
};

const fetchUserNotes = async (
  userId: string,
  { limit, lastEvaluatedKey, includeDeleted }: UseGetUserNotesOptions,
): Promise<GetUserNotesResult> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString
    ? JSON.parse(tokenString)
    : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const params: Record<string, string | number | boolean> = {
    limit: limit ?? DEFAULT_USER_NOTES_LIMIT,
  };
  if (lastEvaluatedKey) params.last_evaluated_key = lastEvaluatedKey;
  if (includeDeleted) params.include_deleted = true;

  const response = await PrivateAdminPanelInstance.get(
    API_ROUTES.GET_USER_NOTES.replace("{user_id}", userId),
    { params },
  );

  const body = response?.data as Partial<ListUserNotesResponse> | undefined;

  return {
    notes: Array.isArray(body?.data) ? (body?.data as Note[]) : [],
    lastEvaluatedKey: body?.last_evaluated_key ?? null,
  };
};

export const useGetUserNotes = (
  userId?: string | null,
  options: UseGetUserNotesOptions = {},
) =>
  useQuery<GetUserNotesResult, Error>({
    queryKey: [
      ...userNotesQueryKey(userId ?? ""),
      options.limit ?? DEFAULT_USER_NOTES_LIMIT,
      options.lastEvaluatedKey ?? null,
      Boolean(options.includeDeleted),
    ],
    queryFn: () => fetchUserNotes(userId as string, options),
    enabled: Boolean(userId),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
